import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { db } from "../../firebase/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { QueryKeys } from "config/query-keys";
import { JobInterface } from "./dto";
import { APPLICATION_STATUS } from "enum/job-status-type";

type Query = {
  isSaved?: boolean;
  isApplied?: boolean;
  userId: string;
};

const toShowinMyAppliedJob = [
  APPLICATION_STATUS.APPROVED,
  APPLICATION_STATUS.REJECTED,
  APPLICATION_STATUS.SHORT_LISTED,
  APPLICATION_STATUS.SUBMITTED
]

/**
 * Fetches the user's saved or applied jobs from Firestore.
 */
const getMyJobs = async ({ isSaved, isApplied, userId }: Query): Promise<JobInterface[]> => {
  try {
    if (!userId) throw new Error("User ID is required to fetch jobs");

    const jobApplicationsRef = collection(db, "applications");

    let jobQuery;


    if (isSaved) {
      jobQuery = query(jobApplicationsRef, where("applicantId", "==", userId), where("status", "==", "SAVED"));
    } else if (isApplied) {
      jobQuery = query(jobApplicationsRef, where("applicantId", "==", userId), where("status", "in", toShowinMyAppliedJob));
    } else {
      return []
    }

    const applicationSnapshot = await getDocs(jobQuery);

    const applicantMap: Record<string, { status: string }> = {};
    const jobIds: string[] = [];


    applicationSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      // console.log(data.status);
      const jobId = data.jobId;
      applicantMap[jobId] = { status: data.status };
      if (!jobIds.includes(jobId)) {
        jobIds.push(jobId);
      }
    });

    if (jobIds.length === 0) return [];

    // Fetch the actual job details from the "jobs" collection
    const jobsCollectionRef = collection(db, "jobs");
    const jobsQuery = query(jobsCollectionRef, where("id", "in", jobIds));

    const jobsSnapshot = await getDocs(jobsQuery);

    return jobsSnapshot.docs.map((doc) => {
      const jobData = doc.data();
      return {
        ...jobData,
        applicant: applicantMap[jobData.id],
        id: doc.id,
      } as JobInterface;
    });
  } catch (error) {
    console.error("Error fetching my jobs:", error);
    throw error;
  }
};

/**
 * React Query hook for fetching user's saved or applied jobs.
 */
export const useGetMyJobs = (params: Query) => {
  const { isApplied, isSaved, userId } = params;

  return useQuery<JobInterface[], AxiosError>({
    queryKey: [QueryKeys.JOBS, isApplied, isSaved, userId],
    queryFn: () => getMyJobs(params),
    enabled: !!userId, // Only run the query if userId exists
  });
};